import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crosshair, Sword, Mountain, ArrowUpRight, Play, Quote, Download, Sparkles, Check, BookOpen, ExternalLink } from "lucide-react";
import { getDailyPrompt } from "../lib/api";
import Logo from "../components/Logo";
import Resources from "./Resources";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/639a096e-1f65-4607-b91b-d74c23fcac10/images/bbce5c1622f3af29cc641ec141c030b94e15edfc8e16cbeba1c2889eb65b8f54.png";
const GRUNGE = "https://static.prod-images.emergentagent.com/jobs/639a096e-1f65-4607-b91b-d74c23fcac10/images/2c078f0d7d0a2eaac6175f6b95a1229b62e0bfa6dc9c7cefbea0737d35737b10.png";
const MOUNTAIN = "https://static.prod-images.emergentagent.com/jobs/639a096e-1f65-4607-b91b-d74c23fcac10/images/404c589619ea6f8bcee2fb5236f965acd3708a4ecb293a61a03e0a657411210a.png";
const FOUNDER_BG = "https://static.prod-images.emergentagent.com/jobs/639a096e-1f65-4607-b91b-d74c23fcac10/images/f91d33950794083b9b00430f5b4b327cac526421b21aa9b7624d61306b33dd15.png";

const PILLARS = [
    {
        Icon: Crosshair,
        title: "AWARE",
        line: "See the war.",
        body: "You cannot fight what you refuse to name. Awareness is the first weapon — the moment you understand what is firing inside your brain and your spirit, the lies lose half their power.",
    },
    {
        Icon: Sword,
        title: "DISCIPLINE",
        line: "Train the fight.",
        body: "Healing is not a feeling. It is a practice. Repetition. Refusal. Replacement. We teach the daily habits — physical, mental, spiritual — that rewire what trauma installed.",
    },
    {
        Icon: Mountain,
        title: "VICTORY",
        line: "Take ground back.",
        body: "Not perfection. Not the absence of struggle. Victory is ground reclaimed — thought by thought, day by day, until the mind that was a prison becomes the place where Christ reigns.",
    },
];

const Home = () => {
    const [prompt, setPrompt] = useState(null);

    useEffect(() => {
        getDailyPrompt().then(setPrompt).catch(() => {});
    }, []);

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section
                className="relative min-h-[100vh] flex items-center overflow-hidden grain"
                data-testid="hero-section"
            >
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${HERO_BG})` }}
                />
                <div className="absolute inset-0 hero-vignette" />
                <div
                    className="absolute inset-0 opacity-30 mix-blend-overlay bg-cover bg-center"
                    style={{ backgroundImage: `url(${GRUNGE})` }}
                />

                <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-32 grid lg:grid-cols-12 gap-8 items-center">
                    <div className="lg:col-span-8 fade-in">
                        <div className="flex items-center gap-3 mb-8 text-xs tracking-[0.4em] text-red-600 font-display uppercase">
                            <span className="h-px w-12 bg-red-600" />
                            Manifesto 01
                        </div>
                        <h1 className="font-display text-white leading-[0.85] tracking-tighter">
                            <span className="block text-5xl sm:text-6xl md:text-7xl lg:text-8xl">THE MIND</span>
                            <span className="block text-5xl sm:text-6xl md:text-7xl lg:text-8xl">IS A</span>
                            <span className="block mt-3">
                                <span className="brush-stroke text-6xl sm:text-7xl md:text-8xl lg:text-9xl px-3">
                                    BATTLEFIELD.
                                </span>
                            </span>
                        </h1>
                        <p className="mt-10 max-w-xl text-white/80 text-base md:text-lg leading-relaxed">
                            For the ones who smile in public while drowning in private. For the men
                            taught to push it down. For anyone who needs more than shallow
                            motivation — you need truth, discipline, and the hope of Christ.
                            <span className="block mt-3 text-white/60 italic">
                                You don't drift into peace. You fight for it.
                            </span>
                        </p>
                        <div className="mt-10 flex flex-wrap gap-4">
                            <Link to="/companion" className="btn-primary" data-testid="hero-cta-companion">
                                Talk to the Companion <ArrowUpRight size={18} />
                            </Link>
                            <Link to="/story" className="btn-ghost" data-testid="hero-cta-story">
                                Read the Story
                            </Link>
                        </div>
                    </div>

                    <div className="hidden lg:flex lg:col-span-4 justify-end fade-in stagger-3">
                        <div className="flex flex-col items-end gap-8">
                            <div className="p-6 bg-red-700/95 w-[260px]">
                                <Logo size={48} color="#fff" />
                                <div className="mt-4 font-display text-3xl tracking-wider text-white">
                                    MINDWORKS
                                </div>
                                <div className="mt-1 text-[10px] tracking-[0.32em] text-white/90">
                                    THINK. FIGHT. TRANSFORM.
                                </div>
                            </div>
                            <div className="flex items-center gap-4 text-white/90">
                                <Crosshair size={20} className="text-red-600" />
                                <span className="font-display tracking-[0.25em] text-sm">AWARE</span>
                                <span className="text-white/30">|</span>
                                <Sword size={20} className="text-red-600" />
                                <span className="font-display tracking-[0.25em] text-sm">DISCIPLINE</span>
                                <span className="text-white/30">|</span>
                                <Mountain size={20} className="text-red-600" />
                                <span className="font-display tracking-[0.25em] text-sm">VICTORY</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* caution stripe bottom */}
                <div className="absolute bottom-0 inset-x-0 h-2 caution-stripes opacity-80" />
            </section>

            {/* MANIFESTO BAR */}
            <section className="border-y border-white/10 bg-black" data-testid="manifesto-bar">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 grid md:grid-cols-3 gap-8 text-center md:text-left">
                    <div>
                        <div className="font-display text-5xl text-red-600">01</div>
                        <div className="mt-2 text-sm tracking-[0.25em] uppercase text-white/80">
                            Fear fights faith.
                        </div>
                    </div>
                    <div>
                        <div className="font-display text-5xl text-red-600">02</div>
                        <div className="mt-2 text-sm tracking-[0.25em] uppercase text-white/80">
                            Lies fight truth.
                        </div>
                    </div>
                    <div>
                        <div className="font-display text-5xl text-red-600">03</div>
                        <div className="mt-2 text-sm tracking-[0.25em] uppercase text-white/80">
                            Despair fights hope.
                        </div>
                    </div>
                </div>
            </section>

            {/* ═══════════════ DAILY BATTLE DRILL — TOP PROMO ═══════════════ */}
            <section
                className="relative overflow-hidden border-y-2 border-red-700/40 bg-gradient-to-br from-red-950/40 via-black to-black"
                data-testid="home-dbd-promo"
            >
                {/* Decorative red glow accents */}
                <div className="absolute -top-32 -left-32 w-[600px] h-[600px] bg-red-700/20 blur-3xl rounded-full pointer-events-none" />
                <div className="absolute -bottom-32 -right-32 w-[600px] h-[600px] bg-red-900/30 blur-3xl rounded-full pointer-events-none" />

                <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-16 md:py-24">
                    <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
                        {/* LEFT — Big sales pitch + CTA */}
                        <div className="lg:col-span-6 order-2 lg:order-1">
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-red-700 text-white font-stencil text-[10px] tracking-[0.3em] mb-6">
                                <Sparkles size={12} /> NEW · INSTANT ACCESS
                            </div>
                            <h2 className="font-display uppercase tracking-tighter leading-[0.85] text-4xl sm:text-5xl md:text-6xl lg:text-7xl">
                                The Daily <br />
                                <span className="text-red-600">Battle Drill.</span>
                            </h2>
                            <p className="mt-6 font-display text-lg md:text-xl uppercase tracking-tight text-white/85">
                                A simple, biblical framework to <span className="text-red-500">win the war</span> in your mind.
                            </p>
                            <p className="mt-5 max-w-xl text-white/65 leading-relaxed">
                                4 short videos. A 5-page workbook. A 30-day challenge. Most people lose the battle in their mind because nobody taught them how to fight. In under 20 minutes, you'll have a real battle plan.
                            </p>

                            {/* What you get — compact bullet list */}
                            <ul className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                                <li className="flex items-start gap-2 text-white/85"><Check size={16} className="text-red-500 mt-0.5 flex-shrink-0" /> 4 video lessons</li>
                                <li className="flex items-start gap-2 text-white/85"><Check size={16} className="text-red-500 mt-0.5 flex-shrink-0" /> 5-page workbook PDF</li>
                                <li className="flex items-start gap-2 text-white/85"><Check size={16} className="text-red-500 mt-0.5 flex-shrink-0" /> Daily Battle Plan worksheet</li>
                                <li className="flex items-start gap-2 text-white/85"><Check size={16} className="text-red-500 mt-0.5 flex-shrink-0" /> 30-Day Renew Challenge</li>
                            </ul>

                            <div className="mt-10 flex flex-wrap items-center gap-4">
                                <Link to="/daily-battle-drill" className="btn-primary text-base" data-testid="home-dbd-cta">
                                    <Sparkles size={18} /> Get It Now — $7 · SUMMER
                                </Link>
                                <div className="flex items-center gap-3 text-white/55 text-xs">
                                    <span className="font-stencil tracking-widest">ONE-TIME · LIFETIME ACCESS</span>
                                </div>
                            </div>
                        </div>

                        {/* RIGHT — Promo image */}
                        <div className="lg:col-span-6 order-1 lg:order-2">
                            <Link to="/daily-battle-drill" className="block group" data-testid="home-dbd-image-link">
                                <div className="relative">
                                    {/* Offset shadow block — tactical aesthetic */}
                                    <div className="absolute -inset-3 bg-red-700/20 -z-10" style={{ transform: "translate(14px, 14px)" }} />
                                    <img
                                        src={`${process.env.REACT_APP_BACKEND_URL}/api/mini-course/promo-hero`}
                                        alt="The Daily Battle Drill — videos, workbook, and tools mockup"
                                        className="w-full h-auto block border-2 border-red-700/50 shadow-[0_0_80px_-20px_rgba(185,28,28,0.6)] group-hover:border-red-600 transition-colors"
                                        loading="eager"
                                        data-testid="home-dbd-image"
                                    />
                                    {/* Floating $7 price tag */}
                                    <div className="absolute -top-4 -right-4 bg-red-700 text-white px-5 py-3 shadow-lg border-2 border-black">
                                        <div className="font-display text-3xl leading-none">$7</div>
                                        <div className="font-stencil text-[9px] tracking-widest text-white/80 mt-1">EXTENDED · AUG</div>
                                    </div>
                                </div>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* MINDWORKS DAILY BATTLE PLAN JOURNAL — Amazon book promo */}
            <section
                className="relative overflow-hidden bg-[#050505] border-b border-white/5"
                data-testid="home-journal-promo"
            >
                {/* Ambient warm glow */}
                <div className="absolute -top-24 right-1/4 w-[520px] h-[520px] bg-amber-700/10 blur-3xl rounded-full pointer-events-none" />
                <div className="absolute -bottom-24 -left-24 w-[520px] h-[520px] bg-red-900/15 blur-3xl rounded-full pointer-events-none" />

                <div className="relative max-w-6xl mx-auto px-6 lg:px-10 py-20 md:py-28">
                    {/* Section label */}
                    <div className="flex items-center justify-center gap-3 mb-6 text-xs tracking-[0.4em] text-amber-500 font-display uppercase">
                        <span className="h-px w-12 bg-amber-500" />
                        New · On Amazon
                        <span className="h-px w-12 bg-amber-500" />
                    </div>

                    <h2 className="text-center font-display text-4xl sm:text-5xl md:text-6xl uppercase tracking-tighter leading-[0.9] mb-4">
                        The pen-and-paper <span className="text-red-600">weapon.</span>
                    </h2>
                    <p className="text-center max-w-2xl mx-auto text-white/70 leading-relaxed mb-12 md:mb-16">
                        The Daily Battle Plan Journal — your morning drill in book form. Focus. Discipline. Overcome. Execute. One page a day.
                    </p>

                    {/* THE BANNER — click anywhere to buy on Amazon */}
                    <a
                        href="https://www.amazon.com/MindWorks-Daily-Battle-Plan-Anguiano/dp/B0H8MWQ27Z/ref=mp_s_a_1_1?crid=2Y03LP5WREXUE&dib=eyJ2IjoiMSJ9.DVvOth0JXnPYVxqQK_qomSYNG2CnkzpcDWCC7XgzMBvGjHj071QN20LucGBJIEps.S545zcigGsbwdmDyOfPbWoewS0PwTCcN-3BqeeJOUl0&dib_tag=se&keywords=mindworks+daily+battle+plan&qid=1784830935&sprefix=mindworks+daily%2Caps%2C254&sr=8-1"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block group relative max-w-md mx-auto"
                        data-testid="journal-banner-link"
                        aria-label="Buy the MindWorks Daily Battle Plan Journal on Amazon"
                    >
                        {/* Offset red shadow block for that gritty MindWorks look */}
                        <div
                            className="absolute -inset-2 bg-red-700/25 -z-10 group-hover:bg-red-700/45 transition-colors duration-300"
                            style={{ transform: "translate(12px, 12px)" }}
                        />
                        <img
                            src={`${process.env.REACT_APP_BACKEND_URL}/api/journal/promo-banner?v=2`}
                            alt="Tim smiling and holding the MindWorks Daily Battle Plan Journal — available on Amazon."
                            className="w-full h-auto block border-2 border-white/10 shadow-[0_25px_80px_-20px_rgba(0,0,0,0.9)] group-hover:border-amber-500/60 transition-all duration-300 group-hover:-translate-y-1"
                            loading="lazy"
                            data-testid="journal-banner-image"
                        />
                        {/* Hover hint overlay — desktop only */}
                        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none hidden md:flex items-center justify-center bg-black/40">
                            <div className="bg-amber-500 text-black px-8 py-4 font-display uppercase tracking-[0.15em] text-lg shadow-2xl flex items-center gap-3">
                                <ExternalLink size={20} />
                                Get it on Amazon · $12.99
                            </div>
                        </div>
                    </a>

                    {/* Mobile-friendly CTA button below the banner */}
                    <div className="mt-10 flex flex-wrap items-center justify-center gap-5">
                        <a
                            href="https://www.amazon.com/MindWorks-Daily-Battle-Plan-Anguiano/dp/B0H8MWQ27Z/ref=mp_s_a_1_1?crid=2Y03LP5WREXUE&dib=eyJ2IjoiMSJ9.DVvOth0JXnPYVxqQK_qomSYNG2CnkzpcDWCC7XgzMBvGjHj071QN20LucGBJIEps.S545zcigGsbwdmDyOfPbWoewS0PwTCcN-3BqeeJOUl0&dib_tag=se&keywords=mindworks+daily+battle+plan&qid=1784830935&sprefix=mindworks+daily%2Caps%2C254&sr=8-1"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-3 px-7 py-4 bg-amber-500 hover:bg-amber-400 text-black font-display tracking-[0.12em] uppercase transition-colors shadow-[0_0_60px_-15px_rgba(245,158,11,0.7)]"
                            data-testid="journal-amazon-cta"
                        >
                            <ExternalLink size={18} />
                            <span className="flex items-baseline gap-3">
                                <span className="text-sm">Get it on Amazon</span>
                                <span className="font-display text-2xl md:text-3xl leading-none">$12.99</span>
                            </span>
                        </a>
                        <div className="flex items-center gap-3 text-white/50 text-xs font-stencil tracking-widest">
                            PAPERBACK · 129 PAGES · SHIPS FROM AMAZON
                        </div>
                    </div>

                    <p className="mt-8 text-center text-[11px] text-white/40 italic max-w-xl mx-auto">
                        &quot;Transformation doesn&apos;t happen by accident — it happens through daily repetition.&quot; — Tim
                    </p>
                </div>
            </section>
            <section className="bg-black py-24 md:py-32" data-testid="featured-video">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="flex items-center gap-3 mb-6 text-xs tracking-[0.4em] text-red-600 font-display uppercase">
                        <span className="h-px w-12 bg-red-600" />
                        Watch
                    </div>
                    <h2 className="font-display text-4xl md:text-6xl uppercase tracking-tighter leading-[0.9]">
                        Listen to <span className="text-red-600">the fight.</span>
                    </h2>
                    <p className="mt-6 max-w-2xl text-white/70 leading-relaxed">
                        Real words for real battles. Press play.
                    </p>

                    <div className="mt-12 relative border border-white/10 bg-[#0a0a0a]">
                        <div className="relative" style={{ paddingBottom: "56.25%" }}>
                            <iframe
                                title="MindWorks featured video"
                                src="https://www.youtube.com/embed/NhnHZJApycQ"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                allowFullScreen
                                className="absolute inset-0 w-full h-full"
                                data-testid="featured-video-iframe"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* ═══════════════ MISSION + TAGLINE ═══════════════ */}
            <section
                className="bg-[#050505] py-24 md:py-32 border-y border-white/10 relative overflow-hidden"
                data-testid="mission-section"
            >
                {/* subtle red glow accent */}
                <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-red-700/10 blur-3xl rounded-full pointer-events-none" />

                <div className="relative max-w-5xl mx-auto px-6 lg:px-10 text-center">
                    {/* Eyebrow */}
                    <div className="flex items-center justify-center gap-3 mb-6 text-xs tracking-[0.4em] text-red-600 font-display uppercase">
                        <span className="h-px w-12 bg-red-600" />
                        The Mission
                        <span className="h-px w-12 bg-red-600" />
                    </div>

                    {/* Tagline / Battle cry */}
                    <h2
                        className="font-display uppercase tracking-tighter leading-[0.95] text-4xl sm:text-5xl md:text-6xl"
                        data-testid="mission-tagline"
                    >
                        Break free from <span className="text-red-600">mental chaos.</span>{" "}
                        Understand <span className="text-red-600">your mind.</span>{" "}
                        Build a <span className="text-red-600">battle plan.</span>{" "}
                        Take back <span className="text-red-600">your life.</span>
                    </h2>

                    {/* Divider */}
                    <div className="mt-12 mb-10 flex items-center justify-center">
                        <span className="h-px w-16 bg-white/15" />
                        <span className="mx-4 font-stencil text-[10px] tracking-[0.5em] text-red-600">★</span>
                        <span className="h-px w-16 bg-white/15" />
                    </div>

                    {/* Mission statement — centered, single column */}
                    <div className="max-w-3xl mx-auto" data-testid="mission-statement">
                        <p className="text-lg md:text-xl text-white/85 leading-relaxed">
                            <span className="font-display tracking-tight text-white">MindWorks</span> helps Christians who feel trapped in depression, anxiety, fear, and mental chaos build a <span className="text-red-500 font-semibold">biblical and practical battle plan</span> for the mind.
                        </p>
                        <p className="mt-6 text-base md:text-lg text-white/70 leading-relaxed">
                            By combining <span className="text-white">Scripture, neuroscience, and proven mental health strategies</span>, you'll learn how destructive thought patterns are formed, how your brain can change through neuroplasticity, and how to take your thoughts captive before they take control of your life.
                        </p>
                        <p
                            className="mt-6 text-base md:text-lg text-white/85 leading-relaxed"
                            data-testid="mission-transformation"
                        >
                            <span className="font-display tracking-tight text-white">Your Transformation:</span> A personalized battle plan that helps you break destructive cycles, strengthen your mind to overcome mental chaos, and develop the skills needed to win the war happening between your ears.
                        </p>
                    </div>

                    {/* Scripture tag — centered */}
                    <div className="mt-12 inline-flex items-center gap-3 px-4 py-2 bg-black border border-red-700/40">
                        <span className="font-stencil text-[10px] tracking-[0.4em] text-red-600">2 CORINTHIANS 10:5</span>
                    </div>
                </div>
            </section>

            {/* SHORTS */}
            <section className="bg-black border-t border-white/10 py-24 md:py-28" data-testid="shorts-section">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
                        <div>
                            <div className="flex items-center gap-3 mb-6 text-xs tracking-[0.4em] text-red-600 font-display uppercase">
                                <span className="h-px w-12 bg-red-600" />
                                Straight from Tim
                            </div>
                            <h2 className="font-display text-4xl md:text-6xl uppercase tracking-tighter leading-[0.9]">
                                Watch <span className="text-red-600">Tim.</span>
                            </h2>
                            <p className="mt-4 max-w-xl text-white/65">
                                Short, direct, no fluff. Real talk from real life — no studio, no script, no agenda except the truth.
                            </p>
                        </div>
                        <a
                            href="https://www.youtube.com/@MindWorksCo"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-ghost text-sm"
                            data-testid="shorts-channel-link"
                        >
                            More on YouTube <ArrowUpRight size={16} />
                        </a>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-px bg-white/10 border border-white/10">
                        {/* PINNED self-hosted reel — Tim's affirmations video */}
                        <div className="bg-black p-5 relative" data-testid="short-pinned">
                            <div className="flex items-center justify-between mb-4 text-xs">
                                <span className="font-stencil tracking-widest text-red-600">★ PINNED</span>
                                <span className="font-stencil text-white/40">9:16</span>
                            </div>
                            <div
                                className="relative bg-[#050505] border border-red-700/50 overflow-hidden"
                                style={{ paddingBottom: "177.77%" }}
                            >
                                <video
                                    src="/freetools-hero.mp4"
                                    poster="/freetools-hero-poster.jpg"
                                    autoPlay
                                    loop
                                    muted
                                    playsInline
                                    controls
                                    preload="metadata"
                                    className="absolute inset-0 w-full h-full object-cover"
                                    data-testid="short-pinned-player"
                                />
                                <div className="absolute top-2 left-2 bg-red-700 px-2 py-0.5 font-stencil text-white text-[9px] tracking-widest pointer-events-none">
                                    LATEST
                                </div>
                            </div>
                            <Link
                                to="/free-tools"
                                className="mt-3 block text-[11px] font-stencil tracking-widest text-white/50 hover:text-red-600 transition-colors"
                                data-testid="short-pinned-cta"
                            >
                                GRAB THE PDF →
                            </Link>
                        </div>

                        {/* Self-hosted reel #2 — Tim's latest short */}
                        <div className="bg-black p-5 relative" data-testid="short-self-02">
                            <div className="flex items-center justify-between mb-4 text-xs">
                                <span className="font-stencil tracking-widest text-red-600">FROM TIM · 02</span>
                                <span className="font-stencil text-white/40">9:16</span>
                            </div>
                            <div
                                className="relative bg-[#050505] border border-red-700/30 overflow-hidden"
                                style={{ paddingBottom: "177.77%" }}
                            >
                                <video
                                    src="/watch-tim-02.mp4"
                                    poster="/watch-tim-02-poster.jpg"
                                    autoPlay
                                    loop
                                    muted
                                    playsInline
                                    controls
                                    preload="metadata"
                                    className="absolute inset-0 w-full h-full object-cover"
                                    data-testid="short-self-02-player"
                                />
                            </div>
                        </div>

                        {[
                            { id: "gL7_1JN5fI8", num: "01" },
                            { id: "Hd1DjnMIuZ8", num: "02" },
                            { id: "5N_xzD67vBo", num: "03" },
                        ].map((s) => (
                            <div
                                key={s.id}
                                className="bg-black p-5"
                                data-testid={`short-${s.id}`}
                            >
                                <div className="flex items-center justify-between mb-4 text-xs">
                                    <span className="font-stencil tracking-widest text-red-600">SHORT · {s.num}</span>
                                    <span className="font-stencil text-white/40">9:16</span>
                                </div>
                                <div
                                    className="relative bg-[#050505] border border-white/5"
                                    style={{ paddingBottom: "177.77%" }}
                                >
                                    <iframe
                                        title={`MindWorks short ${s.num}`}
                                        src={`https://www.youtube.com/embed/${s.id}`}
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                        allowFullScreen
                                        className="absolute inset-0 w-full h-full"
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* PILLARS */}
            <section
                className="relative bg-black py-24 md:py-32 border-t border-white/10"
                data-testid="pillars-section"
            >
                <div
                    className="absolute inset-0 opacity-20 bg-cover bg-center"
                    style={{ backgroundImage: `url(${MOUNTAIN})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black via-black/85 to-black" />
                <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="flex items-center gap-3 mb-6 text-xs tracking-[0.4em] text-red-600 font-display uppercase">
                        <span className="h-px w-12 bg-red-600" />
                        Three Pillars
                    </div>
                    <h2 className="font-display text-4xl md:text-6xl uppercase tracking-tighter leading-[0.9] max-w-3xl">
                        How we <span className="text-red-600">take ground</span> back.
                    </h2>

                    <div className="mt-16 grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
                        {PILLARS.map((p, idx) => (
                            <div
                                key={p.title}
                                className="bg-black p-10 hover:bg-[#0a0a0a] transition-colors group"
                                data-testid={`pillar-${p.title.toLowerCase()}`}
                            >
                                <div className="flex items-start justify-between">
                                    <p.Icon
                                        size={48}
                                        className="text-red-600 group-hover:scale-110 transition-transform"
                                        strokeWidth={1.5}
                                    />
                                    <div className="font-stencil text-white/30 text-sm">
                                        0{idx + 1}
                                    </div>
                                </div>
                                <div className="mt-8 font-display text-4xl tracking-tight">
                                    {p.title}
                                </div>
                                <div className="mt-1 text-red-600 text-sm tracking-[0.2em] uppercase">
                                    {p.line}
                                </div>
                                <p className="mt-6 text-white/70 leading-relaxed">{p.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* DAILY SCRIPTURE */}
            {prompt && (
                <section className="bg-black py-24 border-t border-white/10" data-testid="daily-prompt-section">
                    <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
                        <div className="font-stencil text-red-600 tracking-[0.3em] text-xs mb-6">
                            {prompt.scripture}
                        </div>
                        <blockquote className="font-display text-3xl md:text-5xl tracking-tight leading-[1.05]">
                            <Quote className="inline-block text-red-600 mb-4 mr-2" size={32} />
                            {prompt.text}
                        </blockquote>
                        <p className="mt-10 text-white/60 italic max-w-xl mx-auto">
                            {prompt.reflection}
                        </p>
                    </div>
                </section>
            )}

            {/* COURSES CTA */}
            <section className="bg-[#050505] py-24 md:py-32 border-t border-white/10" data-testid="home-courses-cta">
                <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
                    <div className="flex items-center justify-center gap-3 mb-6 text-xs tracking-[0.4em] text-red-600 font-display uppercase">
                        <span className="h-px w-12 bg-red-600" /> Train For The Battle <span className="h-px w-12 bg-red-600" />
                    </div>
                    <h2 className="font-display text-4xl md:text-6xl uppercase tracking-tighter leading-[0.9]">
                        Two courses. <br />
                        <span className="text-red-600">One mission.</span>
                    </h2>
                    <p className="mt-6 max-w-xl mx-auto text-white/70 leading-relaxed">
                        Start with The Daily Battle Drill (<span className="text-white font-semibold">$7 through Christmas</span>) for a 20-minute crash course — or jump into MindWorks BattleFront ($97) for the full training.
                    </p>
                    <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                        <Link to="/daily-battle-drill" className="btn-primary" data-testid="home-mini-cta">
                            <Sparkles size={16} /> The Daily Battle Drill — $7 · SUMMER
                        </Link>
                        <Link to="/courses" className="btn-ghost" data-testid="home-courses-cta-link">
                            See all courses <ArrowUpRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* FREEBIES */}
            <Resources embedded title="Free Tools for the Fight" />

            {/* QUIZ CTA */}
            <section
                className="relative bg-black py-24 md:py-32 border-t border-white/10 overflow-hidden"
                data-testid="quiz-cta-section"
            >
                <div className="absolute inset-0 caution-stripes opacity-[0.04]" />
                <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
                    <div>
                        <div className="flex items-center gap-3 mb-6 text-xs tracking-[0.4em] text-red-600 font-display uppercase">
                            <span className="h-px w-12 bg-red-600" />
                            Self-Assessment
                        </div>
                        <h2 className="font-display text-4xl md:text-6xl uppercase tracking-tighter leading-[0.9]">
                            Where are you <span className="text-red-600">losing ground?</span>
                        </h2>
                        <p className="mt-8 text-white/70 leading-relaxed max-w-xl">
                            20 questions across 5 fronts — Thoughts, Emotions, Word, Habits, Hope. Brutally honest with yourself = useful result. Get your battle-readiness score, your weakest front, a deep-dive on each pillar, and a verse to plant.
                        </p>
                        <Link to="/quiz" className="mt-10 btn-primary inline-flex" data-testid="home-quiz-cta">
                            Take the Assessment <ArrowUpRight size={18} />
                        </Link>
                    </div>

                    <div className="grid grid-cols-3 gap-px bg-white/10 border border-white/10">
                        {["AWARE", "DISCIPLINE", "VICTORY"].map((p, i) => (
                            <div key={p} className="bg-black p-8 text-center">
                                <div className="font-display text-5xl text-red-600">0{i + 1}</div>
                                <div className="mt-3 font-stencil text-xs tracking-widest text-white/70">{p}</div>
                            </div>
                        ))}
                        <div className="col-span-3 bg-red-700 p-6">
                            <div className="font-stencil text-xs tracking-widest text-white/90">[ APPROX 2 MIN ]</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* LISTEN / WATCH */}
            <section className="bg-black border-t border-white/10 py-20" data-testid="listen-section">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                    <div>
                        <div className="flex items-center gap-4 mb-4 text-xs tracking-[0.4em] text-red-600 font-display uppercase">
                            <span className="h-px w-12 bg-red-600" />
                            Watch & Listen
                        </div>
                        <h2 className="font-display text-3xl md:text-5xl uppercase tracking-tighter leading-[0.9]">
                            Take it <span className="text-red-600">everywhere.</span>
                        </h2>
                        <p className="mt-4 text-white/65 max-w-xl">
                            New videos drop on YouTube. The podcast is on the way. Subscribe so you don't miss the next round.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <a
                            href="https://www.youtube.com/@MindWorksCo"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-5 py-3 border border-red-600 bg-red-700/10 hover:bg-red-700 transition-colors text-xs font-display tracking-[0.2em] uppercase text-white inline-flex items-center gap-2"
                            data-testid="watch-youtube"
                        >
                            ▶ Subscribe on YouTube
                        </a>
                        <div className="px-5 py-3 border border-white/15 text-xs font-stencil tracking-widest text-white/50">SPOTIFY · TBA</div>
                        <div className="px-5 py-3 border border-white/15 text-xs font-stencil tracking-widest text-white/50">APPLE · TBA</div>
                    </div>
                </div>
            </section>

            {/* FREE TOOLS CTA */}
            <section className="bg-black border-t border-white/10 py-24 md:py-32" data-testid="freetools-cta-section">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
                    <div className="flex justify-center lg:justify-start order-2 lg:order-1">
                        <div className="border border-red-700 bg-[#0a0a0a] p-10 w-full max-w-sm relative overflow-hidden">
                            <div className="absolute -top-10 -right-10 w-40 h-40 bg-red-700/20 blur-3xl rounded-full" />
                            <div className="relative">
                                <Download size={56} className="text-red-600" strokeWidth={1.5} />
                                <div className="mt-6 font-display text-7xl leading-none">30</div>
                                <div className="mt-2 text-xs font-stencil tracking-widest text-white/60">BIBLICAL "I AM" AFFIRMATIONS · FREE</div>
                                <div className="mt-6 space-y-2 text-sm text-white/65">
                                    <div className="flex items-center gap-2">
                                        <span className="text-red-600 font-stencil">01</span>
                                        <span>I am loved by God.</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-red-600 font-stencil">02</span>
                                        <span>I am a child of God.</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-red-600 font-stencil">03</span>
                                        <span>I am forgiven.</span>
                                    </div>
                                    <div className="text-white/30 text-xs pl-7">…and 27 more.</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="order-1 lg:order-2">
                        <div className="flex items-center gap-3 mb-6 text-xs tracking-[0.4em] text-red-600 font-display uppercase">
                            <span className="h-px w-12 bg-red-600" />
                            Free Tools
                        </div>
                        <h2 className="font-display text-4xl md:text-6xl uppercase tracking-tighter leading-[0.9]">
                            Speak it until <br />
                            the <span className="text-red-600">lie breaks.</span>
                        </h2>
                        <p className="mt-8 text-white/70 leading-relaxed max-w-xl">
                            The lie was repeated for years. The truth needs to be repeated too. Print these 30 declarations. Speak them out loud. Watch the wiring change.
                        </p>
                        <Link to="/free-tools" className="mt-10 btn-primary inline-flex" data-testid="home-freetools-cta">
                            Get the PDF — Free <ArrowUpRight size={18} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* FOUNDER TEASE */}
            <section
                className="relative bg-black py-24 md:py-32 border-t border-white/10"
                data-testid="founder-tease"
            >
                <div
                    className="absolute inset-0 opacity-15 bg-cover bg-center"
                    style={{ backgroundImage: `url(${FOUNDER_BG})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black" />
                <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-5 flex justify-center lg:justify-start order-2 lg:order-1">
                        <div className="relative">
                            <div className="absolute -inset-2 bg-black/40 -z-10" style={{ transform: "translate(10px, 10px)" }} />
                            <img
                                src="https://customer-assets.emergentagent.com/job_639a096e-1f65-4607-b91b-d74c23fcac10/artifacts/1u153qmf_0DFCD0AB-D749-46C2-A672-6DFEA91E8A2B.png"
                                alt="MindWorks logo"
                                className="w-64 sm:w-72 md:w-80 border border-white/10"
                                data-testid="home-founder-logo-placeholder"
                            />
                        </div>
                    </div>
                    <div className="lg:col-span-7 order-1 lg:order-2">
                        <div className="flex items-center gap-3 mb-6 text-xs tracking-[0.4em] text-red-600 font-display uppercase">
                            <span className="h-px w-12 bg-red-600" />
                            Founder's Story
                        </div>
                        <h2 className="font-display text-4xl md:text-6xl uppercase tracking-tighter leading-[0.9]">
                            I survived a night I wasn't <span className="text-red-600">supposed to.</span>
                        </h2>
                        <p className="mt-8 text-white/75 leading-relaxed max-w-xl">
                            There was a season where the battle in my mind became louder than
                            anything around me. The thoughts told me people would be better off
                            without me. And one night, I almost listened.
                        </p>
                        <p className="mt-4 text-white/60 leading-relaxed max-w-xl italic">
                            But God met me in the dark. And what He pulled me out of, He is now
                            sending me back into — to bring others out with me.
                        </p>
                        <div className="mt-10">
                            <Link to="/story" className="btn-primary" data-testid="founder-read-cta">
                                Read the Full Story <ArrowUpRight size={18} />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA STRIP */}
            <section className="bg-red-700 py-20" data-testid="cta-strip">
                <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
                    <h2 className="font-display text-4xl md:text-6xl uppercase tracking-tighter leading-[0.9] text-white">
                        You don't have to fight alone.
                    </h2>
                    <p className="mt-6 text-white/90 max-w-2xl mx-auto">
                        The Battlefield Companion is here. Compassionate. Grounded in truth and
                        neuroscience. Available the moment you open the page.
                    </p>
                    <div className="mt-10 flex flex-wrap gap-4 justify-center">
                        <Link
                            to="/companion"
                            className="inline-flex items-center gap-2 bg-black text-white px-8 py-4 font-display tracking-[0.18em] uppercase border border-black hover:bg-white hover:text-black transition-colors"
                            data-testid="cta-strip-companion"
                        >
                            <Play size={16} /> Start the Conversation
                        </Link>
                        <Link
                            to="/help"
                            className="inline-flex items-center gap-2 bg-transparent text-white px-8 py-4 font-display tracking-[0.18em] uppercase border border-white/40 hover:bg-white hover:text-red-700 transition-colors"
                            data-testid="cta-strip-help"
                        >
                            Get Help Now
                        </Link>
                    </div>
                </div>
            </section>

            {/* HONEST NOTE / DISCLAIMER */}
            <section className="bg-black border-t border-white/10 py-20 md:py-28" data-testid="homepage-disclaimer">
                <div className="max-w-3xl mx-auto px-6 lg:px-10">
                    <div className="flex items-center gap-3 mb-8 text-xs tracking-[0.4em] text-red-600 font-display uppercase">
                        <span className="h-px w-12 bg-red-600" />
                        A Note Before You Begin
                    </div>

                    <div className="space-y-5 text-white/75 leading-relaxed">
                        <p>Hi, and thank you for being here.</p>

                        <p>
                            I want to be honest with you about who I am and what this is. I'm not a doctor, therapist, psychiatrist, or licensed mental health professional. I don't have any medical or clinical credentials. Everything I share here comes from my own journey through depression and what I've learned, read, and experienced along the way.
                        </p>

                        <p>
                            What you'll find on this site and in these resources is my story and the things that helped me. It is not medical advice, diagnosis, or treatment, and it isn't a substitute for care from a qualified professional. Depression is real and complex, and it can look very different from one person to the next. What worked for me may not be what's right for you, and that's okay.
                        </p>

                        <p>
                            If you're struggling, please don't walk through it alone. I'd encourage you to reach out to a doctor, therapist, or mental health professional who can walk with you personally. If you're already receiving treatment or taking medication, please keep working with your provider and don't make changes based on anything you read or watch here.
                        </p>

                        <p className="border-l-2 border-red-700 pl-5 py-1 text-white/90">
                            If you are in crisis or thinking about harming yourself, please reach out for help right now. In the U.S., you can call or text <a href="tel:988" className="text-red-500 hover:text-red-400 underline decoration-red-700/40 underline-offset-2">988</a> to reach the Suicide and Crisis Lifeline, any time, day or night. If you're outside the U.S., please contact your local emergency services or a crisis line in your country.
                        </p>

                        <p>
                            My hope is simply that something here brings you encouragement, and that you know you're not alone. I'm grateful you're here, and I'm rooting for you.
                        </p>
                    </div>

                    <div className="mt-12 pt-8 border-t border-white/10">
                        <div className="font-signature text-5xl md:text-6xl text-white leading-none tracking-wide" data-testid="homepage-disclaimer-signature">
                            Tim Anguiano
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Home;
